import { ReactNode } from "react";
import "./code-editor-shell.scss";

export type ECodeEditorSideTab = "coach" | "parameters";

/**
 * The right column: Kuika AI and Parameters in the same width, one tab each. Folding and
 * resizing both live on the grip beside it, not in here.
 */
export interface ICodeEditorSidePanelProps {
  activeTab: ECodeEditorSideTab;
  onTabChange: (tab: ECodeEditorSideTab) => void;
  parameterCount: number;
  /** Dragged by the vertical grip, so the column keeps the width the user gave it. */
  width: number;
  /** Null when this datasource has no Kuika AI help; the tab is then not offered at all. */
  coachContent: ReactNode | null;
  /**
   * Null for an editor with nothing to declare — a language action's arguments are typed into
   * its test console, not listed here. The strip then states the one pane instead of offering
   * a choice between two.
   */
  parametersContent: ReactNode | null;
}

export const CodeEditorSidePanel = (props: ICodeEditorSidePanelProps): JSX.Element => {
  const hasParameters: boolean = props.parametersContent !== null;
  const isCoachActive: boolean = (props.activeTab === "coach" || !hasParameters) && props.coachContent !== null;

  return (
    <div className="kuika__code-editor__side" style={{ width: props.width }} data-testid="codeEditorSidePanel">
      <div className="kuika__code-editor__side__tabs">
        <div className="kuika__code-editor__side__segmented">
          {props.coachContent !== null && (
            <button
              type="button"
              className={`kuika__code-editor__side__tab${isCoachActive ? " active" : ""}`}
              onClick={(): void => props.onTabChange("coach")}
              data-testid="coachTab"
            >
              Kuika AI
            </button>
          )}
          <button
            type="button"
            className={`kuika__code-editor__side__tab${isCoachActive ? "" : " active"}`}
            onClick={(): void => props.onTabChange("parameters")}
            data-testid="sideParametersTab"
            hidden={!hasParameters}
          >
            Parameters
            {props.parameterCount > 0 && <span className="kuika__code-editor__badge">{props.parameterCount}</span>}
          </button>
        </div>
      </div>

      <div className="kuika__code-editor__side__body">
        {props.coachContent !== null && (
          <div className={`kuika__code-editor__side__pane${isCoachActive ? "" : " hidden"}`}>{props.coachContent}</div>
        )}
        {hasParameters && (
          <div
            className={`kuika__code-editor__side__pane kuika__code-editor__side__pane--scroll${isCoachActive ? " hidden" : ""}`}
          >
            {props.parametersContent}
          </div>
        )}
      </div>
    </div>
  );
};
